import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics, categories } from '@/mocks/comics';

function formatViews(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + '万';
  return n.toString();
}

export default function ComicPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-300 mb-4">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h2 className="text-xl font-heading text-foreground-900 mb-2">漫画未找到</h2>
          <p className="text-foreground-500 text-sm mb-4">抱歉，您查找的漫画不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const cat = categories.find((c) => c.slug === comic.category);

  return (
    <>
      <main className="pt-16 md:pt-20">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: cat?.name || '分类', href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        {/* Comic Header */}
        <section className="py-8 md:py-12 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-6 md:gap-10">
              <div className="w-40 md:w-56 flex-shrink-0 mx-auto md:mx-0">
                <div className="aspect-[5/7] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={comic.cover}
                    alt={comic.title + ' - 蜜桃少年漫画免费阅读'}
                    title={comic.title + ' - 漫画全集免费阅读下拉式'}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 text-center md:text-left">
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <div className="flex items-center justify-center md:justify-start gap-4 flex-wrap mb-3">
                  <span className="text-sm text-foreground-500">作者：{comic.author}</span>
                  <Link
                    to={`/category/${comic.category}`}
                    className="text-sm text-accent-600 hover:text-accent-500 transition-colors whitespace-nowrap"
                  >
                    {cat?.name}
                  </Link>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-md font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>

                <div className="flex items-center justify-center md:justify-start gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <span className="text-accent-500 text-sm">★</span>
                    <span className="text-sm font-semibold text-foreground-900">{comic.rating}</span>
                    <span className="text-xs text-foreground-400">分</span>
                  </div>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-eye-line mr-1"></i>
                    {formatViews(comic.views)} 阅读
                  </span>
                  <span className="text-sm text-foreground-500">
                    <i className="ri-file-list-line mr-1"></i>
                    {comic.chapters.length} 话
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed mb-5 max-w-xl mx-auto md:mx-0">
                  {comic.description}
                </p>

                <div className="flex items-center justify-center md:justify-start gap-3">
                  <Link
                    to={`/comic/${comic.slug}/read/1`}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <button className="px-6 py-2.5 border border-background-300 text-foreground-700 hover:text-primary-500 hover:border-primary-300 font-medium rounded-full transition-colors text-sm whitespace-nowrap cursor-pointer">
                    <i className="ri-bookmark-line mr-1"></i>收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters List */}
        <section className="py-8 md:py-10" aria-labelledby="chapters-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="chapters-title" className="text-lg md:text-xl font-heading text-foreground-900 mb-5">
              章节列表
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {comic.chapters.map((ch) => (
                <Link
                  key={ch.num}
                  to={`/comic/${comic.slug}/read/${ch.num}`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs font-semibold text-foreground-400 w-8 text-center flex-shrink-0">
                      第{ch.num}话
                    </span>
                    <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors truncate">
                      {ch.title}
                    </span>
                  </div>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{ch.updatedAt}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        <section className="py-8 md:py-10 bg-background-100" aria-labelledby="related-title">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 id="related-title" className="text-lg md:text-xl font-heading text-foreground-900 mb-5">
              猜你喜欢
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
              {comics
                .filter(
                  (c) =>
                    c.id !== comic.id &&
                    (c.category === comic.category || Math.abs(c.rating - comic.rating) < 0.5)
                )
                .slice(0, 5)
                .map((c) => {
                  const cCat = categories.find((cat) => cat.slug === c.category);
                  return (
                    <Link
                      key={c.id}
                      to={`/comic/${c.slug}`}
                      className="group cursor-pointer"
                      data-product-shop=""
                    >
                      <div className="relative overflow-hidden rounded-lg bg-background-200">
                        <div className="aspect-[5/7] overflow-hidden">
                          <img
                            src={c.cover}
                            alt={c.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                      </div>
                      <h3 className="mt-2 text-sm font-semibold text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                        {c.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{c.author}</p>
                    </Link>
                  );
                })}
            </div>
          </div>
        </section>
      </main>

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          description: comic.description,
          url: `https://mitaoshaonian.com/comic/${comic.slug}`,
          inLanguage: 'zh-Hans',
          bookFormat: 'GraphicNovel',
          numberOfPages: comic.chapters.length,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: comic.rating,
            bestRating: 10,
            ratingCount: Math.floor(comic.views / 10),
          },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://mitaoshaonian.com/' },
              { '@type': 'ListItem', position: 2, name: cat?.name || '分类', item: `https://mitaoshaonian.com/category/${comic.category}` },
              { '@type': 'ListItem', position: 3, name: comic.title },
            ],
          },
        })}
      </script>
    </>
  );
}